import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppAccess } from '@/hooks/useAppAccess';

interface AppInstallGuardProps {
  /** The App Registry key to check install status for (e.g., "crm", "chatwoot", "frappe_builder") */
  requiredApp: string;
  children: ReactNode;
  /** Optional fallback to render instead of redirecting to /apps when not installed */
  fallback?: ReactNode;
}

/**
 * Guard component that checks if an app is installed at the tenant level.
 * Redirects to the marketplace (/apps) if the app is not installed,
 * unless a fallback element is provided.
 *
 * This is separate from AppAccessGuard which checks member-level access.
 * Nesting order: AppInstallGuard > AppAccessGuard > ProvisioningGuard
 *
 * Renders children normally if:
 * - Direct auth mode (no CP — local dev)
 * - App access not yet loaded (fail-open to prevent flash)
 * - App is in the tenant's installedApps list
 */
export function AppInstallGuard({ requiredApp, children, fallback }: AppInstallGuardProps) {
  const { isInstalled } = useAppAccess();

  if (!isInstalled(requiredApp)) {
    return fallback ? <>{fallback}</> : <Navigate to="/apps" replace />;
  }

  return <>{children}</>;
}
